import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { supabase } from '@/lib/supabase'
import { useCreateHousehold } from './useCreateHousehold'
import { householdNameSchema } from '@/shared/schemas/householdSchema'

const schema = z.object({ name: householdNameSchema })
type FormData = z.infer<typeof schema>

export function HouseholdSetupForm() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const createHousehold = useCreateHousehold()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(schema), mode: 'onBlur' })

  const onSubmit = async (data: FormData) => {
    createHousehold.mutate(data.name, {
      onSuccess: async () => {
        // Refresh JWT to get household_id claim (CRITICAL)
        await supabase.auth.refreshSession()
        navigate({ to: '/onboarding' })
      },
    })
  }

  return (
    <div className="bg-surface rounded-2xl border border-line p-8 shadow-sm">
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
        <h2 className="text-lg font-semibold text-ink">{t('household.setup.title')}</h2>

        <div className="space-y-1">
          <label htmlFor="household-name" className="block text-sm font-medium text-muted">
            {t('household.setup.nameLabel')}
          </label>
          <input
            id="household-name"
            type="text"
            placeholder={t('household.setup.namePlaceholder')}
            className="w-full rounded-lg border border-line bg-canvas px-3.5 py-2.5 text-ink placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition"
            {...register('name')}
          />
          {errors.name && (
            <span role="alert" className="block text-xs text-danger mt-1">
              {t('household.error.nameRequired')}
            </span>
          )}
        </div>

        {createHousehold.error && (
          <div role="alert" className="rounded-lg bg-danger/10 border border-danger/20 px-3.5 py-2.5 text-sm text-danger">
            {createHousehold.error.message.includes('409')
              ? t('household.error.alreadyMember')
              : t('common.error')}
          </div>
        )}

        <button
          type="submit"
          disabled={createHousehold.isPending}
          className="w-full rounded-lg bg-accent text-white font-semibold py-2.5 px-4 hover:bg-accent-dark active:bg-accent-dark focus:outline-none focus:ring-2 focus:ring-accent transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('household.setup.submitButton')}
        </button>
      </form>
    </div>
  )
}
